// AccountClosuresView — admin vê e aciona pedidos de encerramento de conta.
// Processar = marca concluído + suspende a conta (bloqueia login, reversível).
// [[project-account-closure]]

import { useEffect, useState } from "preact/hooks";
import { AlertTriangle, DoorOpen, Loader2, RefreshCw, RotateCcw, UserX } from "lucide-react";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { api, type AccountClosureRow } from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

type Filter = "requested" | "processed" | "cancelled" | "all";

const STATUS_BADGE: Record<string, string> = {
  requested: "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300",
  processed: "border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  cancelled: "border-border bg-muted text-muted-foreground"
};
const STATUS_LABEL: Record<string, string> = {
  requested: "Solicitado",
  processed: "Processado",
  cancelled: "Cancelado"
};

export function AccountClosuresView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [rows, setRows] = useState<AccountClosureRow[]>([]);
  const [filter, setFilter] = useState<Filter>("requested");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { rows } = await api.accountClosuresList(filter === "all" ? undefined : filter);
      setRows(rows);
      setPhase("ready");
    } catch (err) {
      setErrMsg(friendlyError(err));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [filter]);

  async function process(r: AccountClosureRow) {
    const ok = await openConfirm({
      title: "Processar encerramento?",
      message:
        `Isso conclui o pedido de ${r.email ?? r.userId} e SUSPENDE a conta (bloqueia o login). É reversível — você pode reativar depois.`,
      confirmLabel: "Processar e suspender",
      cancelLabel: "Cancelar",
      danger: true
    });
    if (!ok) return;
    setBusy(r.id);
    try {
      await api.accountClosureAction(r.id, "process");
      await load();
    } catch (err) {
      await openAlert({ title: "Erro", message: friendlyError(err) });
    } finally {
      setBusy(null);
    }
  }

  async function reactivate(r: AccountClosureRow) {
    const ok = await openConfirm({
      title: "Reativar conta?",
      message: `Remove a suspensão de ${r.email ?? r.userId} e permite o login novamente.`,
      confirmLabel: "Reativar",
      cancelLabel: "Cancelar"
    });
    if (!ok) return;
    setBusy(r.id);
    try {
      await api.accountClosureAction(r.id, "reactivate");
      await load();
    } catch (err) {
      await openAlert({ title: "Erro", message: friendlyError(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <DoorOpen className="size-5 text-violet-500" />
          <div>
            <h1 class="text-lg font-bold">Encerramentos de conta</h1>
            <p class="text-xs text-muted-foreground">
              Pedidos de encerramento. Processar conclui o pedido e suspende o
              login (reversível); até 30 dias conforme os Termos.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <RefreshCw className="size-3.5" /> Atualizar
        </button>
      </header>

      <div class="flex flex-wrap gap-1">
        {(
          [
            ["requested", "Solicitados"],
            ["processed", "Processados"],
            ["cancelled", "Cancelados"],
            ["all", "Todos"]
          ] as const
        ).map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            class={
              "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
              (filter === f
                ? "border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "border-border hover:bg-accent")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {errMsg && (
        <div class="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="size-4" /> {errMsg}
        </div>
      )}

      {phase === "loading" ? (
        <div class="flex items-center gap-2 p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </div>
      ) : rows.length === 0 ? (
        <p class="rounded-md border border-border bg-card/60 p-6 text-center text-sm text-muted-foreground">
          Nenhum pedido nesta categoria.
        </p>
      ) : (
        <ul class="space-y-2">
          {rows.map((r) => (
            <li
              key={r.id}
              class="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card/60 p-3 text-sm"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="font-semibold">{r.name || r.email || r.userId}</span>
                  <span
                    class={
                      "rounded border px-1.5 py-0.5 text-[10px] font-medium " +
                      (STATUS_BADGE[r.status] ?? "")
                    }
                  >
                    {STATUS_LABEL[r.status] ?? r.status}
                  </span>
                  {r.suspended && (
                    <span class="inline-flex items-center gap-1 rounded border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                      <UserX className="size-3" /> Suspenso
                    </span>
                  )}
                </div>
                <div class="mt-0.5 text-xs text-muted-foreground">{r.email}</div>
                <div class="mt-0.5 text-xs text-muted-foreground">
                  Solicitado {new Date(r.requestedAt).toLocaleString("pt-BR")}
                  {r.processedAt
                    ? ` · processado ${new Date(r.processedAt).toLocaleString("pt-BR")}`
                    : ""}
                </div>
                {r.reason && (
                  <div class="mt-1 break-words text-xs text-foreground">
                    Motivo: {r.reason}
                  </div>
                )}
              </div>
              <div class="flex shrink-0 flex-wrap gap-2">
                {r.status === "requested" && (
                  <button
                    type="button"
                    onClick={() => void process(r)}
                    disabled={busy === r.id}
                    class="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                  >
                    <UserX className="size-3.5" /> Processar e suspender
                  </button>
                )}
                {r.suspended && (
                  <button
                    type="button"
                    onClick={() => void reactivate(r)}
                    disabled={busy === r.id}
                    class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    <RotateCcw className="size-3.5" /> Reativar
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
